import React, { useEffect, useState } from 'react';
import { resourcesAPI } from './api';
import { AdvancedSearch } from './AdvancedSearch';
import { VideoPlayer } from './VideoPlayer';
import './gpj-pages.css';

const RESOURCE_CATEGORIES = [
  { title: 'Case law database', type: 'case_law', description: 'Search Ontario family-law decisions in the library.' },
  { title: 'Legal guides', type: 'legal_guide', description: 'Guides and procedural information for self-represented parents.' },
  { title: 'Sample documents', type: 'form_template', description: 'Examples and templates available through the resource library.' },
  { title: 'Video tutorials', type: 'video_tutorial', description: 'Educational videos and recorded workshops.' },
  { title: 'Legal articles', type: 'article', description: 'Articles and updates included in the library.' }
];

const formatResourceType = (type = 'resource') => type
  .split('_')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

function ResourceCard({ resource }) {
  const rating = Number(resource.rating);

  return (
    <article className="gpj-resource-card">
      {resource.resource_type === 'video_tutorial' && resource.url ? (
        <VideoPlayer
          videoUrl={resource.url}
          title={resource.title}
          description={resource.description}
        />
      ) : (
        <>
          <div className="gpj-resource-card__head">
            <span className="gpj-resource-kind">{formatResourceType(resource.resource_type)}</span>
            <h3>{resource.title}</h3>
            <div className="gpj-resource-meta">
              {resource.category && <span>Category: {resource.category}</span>}
              {resource.author && <span>By: {resource.author}</span>}
            </div>
          </div>

          {resource.description && <p className="gpj-resource-description">{resource.description}</p>}

          {resource.tags?.length > 0 && (
            <div className="gpj-resource-tags" aria-label="Resource tags">
              {resource.tags.map((tag) => <span key={tag}>{tag}</span>)}
            </div>
          )}

          <div className="gpj-resource-meta">
            {Number.isFinite(Number(resource.views)) && <span>{Number(resource.views)} views</span>}
            {Number.isFinite(rating) && rating > 0 && <span>{rating.toFixed(1)} rating</span>}
            {resource.date_published && <span>Published {new Date(resource.date_published).toLocaleDateString()}</span>}
          </div>
        </>
      )}

      {resource.url && resource.resource_type !== 'video_tutorial' && (
        <a className="gpj-btn gpj-btn--navy gpj-btn--sm gpj-resource-link" href={resource.url} target="_blank" rel="noopener noreferrer">
          View resource ↗
        </a>
      )}
    </article>
  );
}

export default function ResourceLibraryPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    let active = true;

    const loadResources = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await resourcesAPI.getAll(null, null, null, 50);
        if (active) setResources(response.data);
      } catch {
        if (active) setError('Resources could not be loaded. Please try again later.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadResources();
    return () => { active = false; };
  }, []);

  const browseCategory = async (resourceType) => {
    setLoading(true);
    setError('');
    try {
      const response = await resourcesAPI.getAll(resourceType, null, null, 50);
      setSearchResults(response.data);
    } catch {
      setError('That resource category could not be loaded. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setSearchResults(null);
    setError('');
  };

  const displayResources = searchResults ?? resources;

  return (
    <section className="gpj-section" aria-labelledby="resource-library-title">
      <div className="gpj-wrap">
        <div className="gpj-sec-head">
          <span className="gpj-eyebrow">Resources</span>
          <h2 id="resource-library-title">Family law resource library</h2>
          <p>Search the existing GPJ collection of legal resources, cases, guides, and educational materials.</p>
        </div>

        <div className="gpj-resource-search">
          <AdvancedSearch onResults={(results) => { setSearchResults(results); setError(''); }} />
        </div>

        <div className="gpj-resource-categories" aria-label="Resource categories">
          {RESOURCE_CATEGORIES.map((category) => (
            <article key={category.type} className="gpj-card">
              <span className="gpj-card__kicker">Resource type</span>
              <h3>{category.title}</h3>
              <p className="gpj-topic-description">{category.description}</p>
              <button type="button" className="gpj-btn gpj-btn--ghost gpj-btn--sm gpj-topic-action" onClick={() => browseCategory(category.type)}>
                Browse {category.title}
              </button>
            </article>
          ))}
        </div>

        <div className="gpj-resource-results" aria-live="polite" aria-busy={loading}>
          <div className="gpj-results-head">
            <h3>{searchResults === null ? 'All resources' : 'Filtered resources'}</h3>
            {searchResults !== null && (
              <button type="button" className="gpj-btn gpj-btn--ghost gpj-btn--sm" onClick={clearResults}>Clear filters</button>
            )}
          </div>

          {loading && <p className="gpj-loading" role="status">Loading resources…</p>}
          {!loading && error && <p className="gpj-error" role="alert">{error}</p>}

          {!loading && !error && (
            <>
              <p className="gpj-result-count">{displayResources.length} found</p>
              <div className="gpj-resource-list">
                {displayResources.map((resource) => <ResourceCard key={resource.id} resource={resource} />)}
              </div>
              {displayResources.length === 0 && <p className="gpj-empty" role="status">No resources match the current search and filters.</p>}
            </>
          )}
        </div>

        <p className="gpj-disclaimer">
          <b>Check the source and publication date.</b> Resource-library material is general
          information and may not reflect a recent rule or form change. Confirm procedural
          requirements using the linked official source before acting.
        </p>
      </div>
    </section>
  );
}
